/**
 * AIHW Quality Indicator (QI) quarterly submission clock.
 *
 * The register marks this UNVERIFIED as a primary-source instrument (it is
 * DoHDA QI Program Manual 4.0 Part A regulator guidance, not confirmed Act/
 * Rule text) — classified TARGET. Submission-state (has this facility/
 * quarter already been submitted?) is a database read owned by the caller;
 * this function only classifies urgency for an UNSUBMITTED quarter.
 */
import { getCurrentQiSubmissionWindow, type QiQuarterKey } from '../award-data/qi';
import type { ComplianceDeadlineItem } from './types';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const PROVISION =
  'Statura operational target (DoHDA QI Program Manual 4.0 Part A §3 — regulator guidance, not verified as primary Act/Rule text)';

export interface QiSubmissionFacilityRow {
  facilityId: string;
  timezone: string | null;
}

export type QiSubmissionBand = 'EARLY_HEADS_UP' | 'APPROACHING' | 'OVERDUE' | 'OUT_OF_WINDOW';

export interface QiSubmissionEvaluation {
  item: ComplianceDeadlineItem;
  band: QiSubmissionBand;
  quarter: QiQuarterKey;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  daysUntilDue: number;
}

/**
 * Bands mirror the cron: >30d or <-30d out of window (no item); 15–30d early
 * heads-up; 0–14d approaching; overdue once the 21st has passed.
 */
export function evaluateQiSubmissionWindow(
  row: QiSubmissionFacilityRow,
  now: Date,
): QiSubmissionEvaluation {
  const tz = row.timezone ?? 'Australia/Sydney';
  const window = getCurrentQiSubmissionWindow(now, tz);
  const dueMs = new Date(`${window.dueDate}T00:00:00+00:00`).getTime();
  const daysUntilDue = Math.round((dueMs - now.getTime()) / MS_PER_DAY);

  const band: QiSubmissionBand =
    daysUntilDue > 30 || daysUntilDue < -30
      ? 'OUT_OF_WINDOW'
      : daysUntilDue > 14
        ? 'EARLY_HEADS_UP'
        : daysUntilDue >= 0
          ? 'APPROACHING'
          : 'OVERDUE';

  return {
    band,
    quarter: window.quarter,
    periodStart: window.periodStart,
    periodEnd: window.periodEnd,
    dueDate: window.dueDate,
    daysUntilDue,
    item: {
      id: `qi-submission:${row.facilityId}:${window.quarter}:${window.periodStart}`,
      register: 'QI_SUBMISSION',
      recordRef: row.facilityId,
      title: 'AIHW Quality Indicator quarterly submission',
      dueAt: window.dueDate,
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      status:
        band === 'OVERDUE'
          ? 'OVERDUE'
          : band === 'APPROACHING'
            ? 'DUE_SOON'
            : band === 'EARLY_HEADS_UP'
              ? 'UPCOMING'
              : 'UPCOMING',
      facilityId: row.facilityId,
      windowStart: window.periodStart,
      href: '/compliance/quality/qi-data-entry/submit',
      meta: {
        daysUntilDue,
        quarter: window.quarter,
        periodStart: window.periodStart,
        periodEnd: window.periodEnd,
      },
    },
  };
}
